import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-image-crop/dist/ReactCrop.css';
import { HelmetProvider } from 'react-helmet-async';
import * as serviceWorker from './serviceWorker';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  ApolloProvider,
  ApolloClient,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import type { NormalizedCacheObject } from '@apollo/client';

import { SsrContext } from './ssrContext';
import { ScrollToTop } from './ScrollToTop';
import TENANT_TO_LANG from './common/TENANT_TO_LANG';
import GasteArsivi, { type Tenant, type Lang } from './App';

declare global {
  interface Window {
    __APOLLO_STATE__?: NormalizedCacheObject;
  }
}

// Initialize Sentry if DSN is provided
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 1.0,
  });
}

const host = window.location.host;
const gaRegex = /.*.gastearsivi.com/;
const tenant: Tenant = host.match(gaRegex) ? 'gastearsivi' : 'historyworm';

const lang: Lang = TENANT_TO_LANG[tenant];

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_GA_API_URL}/graphql`,
  credentials: 'include',
});

// Send current page URL so session middleware can store landing page for goal attribution
const landingPageLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-landing-page':
      typeof window !== 'undefined' ? window.location.href : '',
  },
}));

const gqlClient = new ApolloClient({
  cache: new InMemoryCache().restore(window.__APOLLO_STATE__),
  link: landingPageLink.concat(httpLink),
});

const AppContainer: React.FC = () => (
  <HelmetProvider>
    <ApolloProvider client={gqlClient}>
      <Router>
        <ScrollToTop />
        <SsrContext.Provider value={{ isSsr: false, context: {} }}>
          <GasteArsivi lang={lang} tenant={tenant} />
        </SsrContext.Provider>
      </Router>
    </ApolloProvider>
  </HelmetProvider>
);

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}
if (rootEl.childNodes.length > 0) {
  hydrateRoot(rootEl, <AppContainer />);
} else {
  createRoot(rootEl).render(<AppContainer />);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
